import * as React from 'react';
import { AvailabilitySearchResultSeatingAreaDialog } from 'components/www/availability-search/AvailabilitySearchResultSeatingAreaDialog';
import { Button, Typography } from 'omakase';
import { DisplayMoney } from '@tock/money';
import { DisplayTime } from '@tock/time';
import { LocalDateTime } from 'js-joda';
import { LockOptions } from 'types';
import { consumer, core } from '@tock/coretypes';
import { getHasNonZeroEffectivePrice } from 'utils/priceDisplayUtils';
import {
  priceTypography,
  timeItem,
} from 'components/www/availability-search/timeAndSizeItemCsssDefs';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useState } from 'react';

interface Props {
  locale: string;

  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  onClick(
    dateTime: LocalDateTime,
    priceCents: number,
    partySize: number,
    lockOptions?: Partial<LockOptions>
  ): void;
  partySize: number;
  price: number;
  minRequiredSpend: {
    totalCents: number;
    priceDisplayDistribution?: core.TicketPriceInformation.PriceDistribution;
  };
  priceDisplayStyle: consumer.OfferedPriceSummary.PriceDisplayStyle;
  time: string;
  timeZone: string;
  business: consumer.IBusinessConfig;
  offering: consumer.Offering;
  seatingAreas: core.SeatingArea[];
}

const TimeAndSizeItem: React.FunctionComponent<Props> = ({
  locale,
  lockedDateTime,
  lockedSeatingArea,
  onClick,
  partySize,
  price,
  minRequiredSpend: { totalCents: minRequiredSpendCents, priceDisplayDistribution },
  priceDisplayStyle,
  time,
  timeZone,
  business,
  offering,
  seatingAreas,
}) => {
  const hasSeatingAreasConsumerSearch = useFeatureFlagForConsumer('SEATING_AREAS_CONSUMER_SEARCH');
  const displayMinRequiredPrice = priceDisplayStyle === 'MIN_REQUIRED_SPEND';
  const displayPurchaseSize = !displayMinRequiredPrice || priceDisplayDistribution !== 'PER_PARTY';

  const [showChooseSeatingAreaModal, setShowChooseSeatingAreaModal] = useState(false);
  const handleSelectSeatingArea = (seatingAreaId: number) => {
    setShowChooseSeatingAreaModal(false);
    handleSelectDateAndTime(seatingAreaId);
  };

  const handleSelectDateAndTime = (seatingAreaId?: number) => {
    const lockOptions: Partial<LockOptions> = {};
    if (seatingAreaId) {
      lockOptions.seatingAreaId = seatingAreaId;
    }
    onClick(LocalDateTime.parse(time), price || 0, partySize, lockOptions);
  };

  const handleClick = () => {
    const hasSingleSeatingArea = seatingAreas.length === 1;
    const hasMultipleSeatingAreas = seatingAreas.length > 1;
    const isSeatingAreaOptionsFlagEnabled =
      hasSeatingAreasConsumerSearch && seatingAreas.length > 0;

    if (isSeatingAreaOptionsFlagEnabled) {
      if (hasSingleSeatingArea) {
        // Select single seating area on lock request
        handleSelectDateAndTime(seatingAreas[0].id);
      } else if (hasMultipleSeatingAreas) {
        setShowChooseSeatingAreaModal(true);
      }
    } else {
      handleSelectDateAndTime();
    }
  };

  const timeValue = LocalDateTime.parse(time).toLocalTime();
  const isLockedItem = !!lockedDateTime && !!time && time === lockedDateTime;
  const hasNonZeroEffectivePrice = getHasNonZeroEffectivePrice(
    price,
    minRequiredSpendCents,
    priceDisplayStyle
  );
  const renderPrice = hasNonZeroEffectivePrice;
  const purchaseSizeDisplay = <span> × {partySize}</span>;
  const hasSeatingAreasAvailable = hasSeatingAreasConsumerSearch && seatingAreas.length > 0;

  return (
    <div css={timeItem}>
      <Button
        size="large"
        variant={isLockedItem ? 'success' : 'primary'}
        data-testid="select-time"
        onClick={handleClick}
        aria-haspopup={hasSeatingAreasAvailable && 'dialog'}
      >
        <DisplayTime localeFromProps={locale} zone={timeZone} timeValue={timeValue} />
      </Button>
      {renderPrice && (
        <Typography variant="caption" color="fgSubtle" css={priceTypography}>
          <DisplayMoney
            amount={displayMinRequiredPrice ? minRequiredSpendCents : price}
            roundIfWholeNumber={true}
          />
          {displayPurchaseSize && purchaseSizeDisplay}
        </Typography>
      )}
      {hasSeatingAreasAvailable && (
        <AvailabilitySearchResultSeatingAreaDialog
          business={business}
          dateTimeValue={LocalDateTime.parse(time)}
          isOpen={showChooseSeatingAreaModal}
          lockedSeatingArea={isLockedItem ? lockedSeatingArea : undefined}
          offering={offering}
          onClose={() => {
            setShowChooseSeatingAreaModal(false);
          }}
          onSelectSeatingArea={handleSelectSeatingArea}
          partySize={partySize}
          seatingAreas={seatingAreas}
          variant="sheet"
        />
      )}
    </div>
  );
};

export default TimeAndSizeItem;
